/**
 * lib/auth/AuthContext.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * React context for Supabase authentication.
 *
 * Design constraints
 * ──────────────────
 * - Anonymous mode: when Supabase is not configured, loading is false from the
 *   start, user/session are null, and sign-in methods are silent no-ops.
 * - On SIGNED_IN: merges local profileStore data into Supabase (fire-and-forget).
 * - Compatible with future providers: signInWithGoogle is the only provider-
 *   specific method; future methods (Apple, email) can be added without
 *   changing the context shape.
 *
 * Usage
 * ─────
 *   // Wrap your app root:
 *   <AuthProvider><App /></AuthProvider>
 *
 *   // Consume anywhere:
 *   const { user, signInWithGoogle, signOut } = useAuth();
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
// Sprint: Product Analytics V1 — auth_started/auth_completed. WHICH method
// was attempted and WHETHER the call itself succeeded — never an email,
// name, or Supabase user id (neither auth method below ever passes one).
import { logMarketplaceEvent } from "@/lib/marketplaceEventLog";

// ─── Context shape ────────────────────────────────────────────────────────────

export interface AuthContextValue {
  /** Authenticated Supabase user, or null for anonymous sessions. */
  user:             User | null;
  /** Full session object (contains access_token, etc.), or null. */
  session:          Session | null;
  /** True only during the initial auth-state resolution on mount. */
  loading:          boolean;
  /** Trigger Google OAuth popup/redirect via Supabase. */
  signInWithGoogle: () => Promise<void>;
  /**
   * Sprint: Auth Foundation V1 — email magic link (passwordless), the V1
   * fallback for when Google OAuth isn't configured in this Supabase
   * project. Returns a safe, translatable error CODE (never the raw
   * Supabase error message, which can echo back input) — the caller
   * decides how to localize/display it. Resolves with `{ error: null }`
   * once Supabase has accepted the request, i.e. "check your email", not
   * once the user has actually clicked the link.
   */
  signInWithEmail:  (email: string) => Promise<{ error: string | null }>;
  /** Sign out and clear session. Local profile is NOT cleared. */
  signOut:          () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user:             null,
  session:          null,
  loading:          false,
  signInWithGoogle: async () => {},
  signInWithEmail:  async () => ({ error: "auth_not_configured" }),
  signOut:          async () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  // Start loading=true only when Supabase is configured (avoids flash)
  const [loading, setLoading] = useState(!!supabase);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    // ── Restore session from storage on mount ──────────────────────────────
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // ── Listen for auth state changes (login, logout, token refresh) ───────
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // On fresh login, merge local profile data into Supabase
      if (event === "SIGNED_IN" && session?.user) {
        try {
          const [{ syncProfileToSupabase }, { loadProfile }] = await Promise.all([
            import("@/lib/auth/profileSync"),
            import("@/lib/profile/profileStore"),
          ]);
          await syncProfileToSupabase(session.user.id, loadProfile());
        } catch (err) {
          // Non-fatal — local profile still works
          console.warn("[AuthContext] Profile sync failed:", err);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Auth methods ───────────────────────────────────────────────────────────

  // Return to the current page after auth so the user never loses context.
  // E.g. /experiences?q=tokyo → after login → back to /experiences?q=tokyo
  // (The ?q= auto-trigger then fires and the sequence continues seamlessly.)
  // Sprint: Auth Foundation V1 — landing from "/" now goes to the new
  // /cuenta account page instead of the legacy, unlinked /dashboard
  // (see lib/auth/profileSync.ts's own PROFILE_SYNC_ENABLED=false doc —
  // /dashboard's own backing tables don't exist in production).
  function postAuthReturnTo(): string {
    return window.location.pathname === "/"
      ? "/cuenta"
      : window.location.pathname + window.location.search;
  }

  const signInWithGoogle = async (): Promise<void> => {
    if (!supabase) {
      console.warn("[AuthContext] Supabase not configured — login unavailable.");
      return;
    }
    // Sprint: Product Analytics V1 — auth_started fires unconditionally
    // (the attempt itself), before the redirect call. auth_completed here
    // means "the redirect call itself resolved without a client-side
    // error" — signInWithOAuth navigates the browser away almost
    // immediately, so there's no synchronous signal for the full OAuth
    // round-trip; that's a known, documented limitation (see
    // docs/operations/PRODUCT_ANALYTICS_V1.md), not a claim of confirmed
    // login. Never touches onAuthStateChange — zero risk to the existing
    // SIGNED_IN/profile-sync behavior above.
    logMarketplaceEvent("auth_started", { auth_method: "google" });
    try {
      await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}${postAuthReturnTo()}`,
        },
      });
      logMarketplaceEvent("auth_completed", { auth_method: "google", success: true });
    } catch (err) {
      logMarketplaceEvent("auth_completed", { auth_method: "google", success: false });
      throw err;
    }
  };

  const signInWithEmail = async (email: string): Promise<{ error: string | null }> => {
    if (!supabase) {
      return { error: "auth_not_configured" };
    }
    const trimmed = email.trim();
    if (!trimmed) {
      return { error: "auth_email_required" };
    }
    // Sprint: Product Analytics V1 — same auth_started/auth_completed
    // pair as signInWithGoogle above. Never passes `trimmed` (the email
    // itself) to logMarketplaceEvent — only the method name and the
    // already-computed success boolean.
    logMarketplaceEvent("auth_started", { auth_method: "magic_link" });
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmed,
      options: {
        emailRedirectTo: `${window.location.origin}${postAuthReturnTo()}`,
      },
    });
    logMarketplaceEvent("auth_completed", { auth_method: "magic_link", success: !error });
    // Safe, allowlisted error code only — never Supabase's own raw message
    // (which can echo the submitted email back) and never logged.
    return { error: error ? "auth_email_failed" : null };
  };

  const signOut = async (): Promise<void> => {
    if (!supabase) return;
    await supabase.auth.signOut();
    // Note: local profileStore is intentionally NOT cleared on sign-out
    // so anonymous sessions can continue using accumulated preferences.
  };

  return (
    <AuthContext.Provider
      value={{ user, session, loading, signInWithGoogle, signInWithEmail, signOut }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
